/* eslint-disable max-lines-per-function */
import {
  Form,
  Input,
  Select,
  Textarea,
  Button,
  Cascader,
  InputNumber,
  Radio,
} from '@seiue/ui'
import { Col } from 'antd'
import React from 'react'
import styled from 'styled-components'

import { DatePicker } from 'packages/components/DatePicker'
import { DatePickerRange } from 'packages/components/DatePickerRange'
import { GrowpIcon } from 'packages/components/GrowpIcon'
import { DateTypeEnum } from 'packages/sdks-next/growp'
import {
  customizeCountValidator,
  getPcaRegionOption,
  IFormItemTypeEnum,
} from 'packages/utils'

import { getTheme } from '@/utils/theme'

import { IConfig, RangePlaceholder, ViewFormProps } from '../types'
import {
  getExtraInfo,
  getItemRenderRules,
  getShowData,
  normFile,
  selectValuePropsName,
} from '../utils'

import { FileUpload } from './FileUpload'
const { Item } = Form
const theme = getTheme()

/**
 * 检查表单
 *
 * @param props - 参数
 * @param props.props - 参数
 * @returns 组件
 */
export const ViewForm: React.FC<ViewFormProps> = (props) => {
  const { isEdit, configs, initData = {}, viewNotForm = false, colSpan } = props

  /**
   * 获取Form.Item内部组件
   *
   * @param i - 表单配置项
   * @returns 渲染的表单项内组件
   */
  const selectItem = (i: IConfig) => {
    const disabled = !!i.disabled || !isEdit
    const placeholder = isEdit ? (disabled ? '':`请输入`) : undefined
    const selectPlaceholder = isEdit ? (disabled ? '':`请选择`) : undefined
    const rangePlaceholder = isEdit ? (disabled ? ['',''] as RangePlaceholder : ['开始时间', '结束时间'] as RangePlaceholder ) : undefined
    switch (i.type) {
      case IFormItemTypeEnum.Input:
        return (
          <Input
            disabled={disabled}
            placeholder={placeholder}
            maxLength={99}
          />
        )
      case IFormItemTypeEnum.Select:
        return (
          <Select
            allowClear={true}
            disabled={disabled}
            placeholder={selectPlaceholder}
          >
            {i.options
              ? i.options.map((option) => {
                  return (
                    <Select.Option
                      key={option.value}
                      value={option.value}
                      style={{
                        display: option.disabled ? 'none' : undefined,
                      }}
                    >
                      {option.label}
                    </Select.Option>
                  )
                })
              : null}
          </Select>
        )
      case IFormItemTypeEnum.Multi:
        return (
          <Select
            showArrow={true}
            showSearch={false}
            allowClear={true}
            mode="multiple"
            disabled={disabled}
            placeholder={selectPlaceholder}
          >
            {i.options
              ? i.options.map((option) => {
                  return (
                    <Select.Option
                      key={option.value}
                      value={option.value}
                      style={{
                        display: option.disabled ? 'none' : undefined,
                      }}
                    >
                      {option.label}
                    </Select.Option>
                  )
                })
              : null}
          </Select>
        )
      case IFormItemTypeEnum.Date: {
        return (
          <DatePicker
            dateType={DateTypeEnum.Date}
            disabled={disabled}
            placeholder={selectPlaceholder}
          />
        )
      }
      case IFormItemTypeEnum.Time:
        return (
          <DatePicker
            dateType={i.metadata?.dateType || DateTypeEnum.Time}
            disabled={disabled}
            placeholder={selectPlaceholder}
          />
        )
      case IFormItemTypeEnum.DateRange: {
        return (
          <DatePickerRange
            dateType={i.metadata?.dateType}
            disabled={disabled}
            placeholder={rangePlaceholder}
          />
        )
      }
      case IFormItemTypeEnum.TextArea:
        return (
          <Textarea
            disabled={disabled}
            placeholder={placeholder}
            maxLength={9999}
            autoSize={{ minRows: 2 }}
          />
        )
      case IFormItemTypeEnum.Address: {
        return (
          <Cascader
            disabled={disabled}
            options={getPcaRegionOption()}
            placeholder={selectPlaceholder}
          />
        )
      }
      case IFormItemTypeEnum.Integer: {
        return (
          <InputNumber
            disabled={disabled}
            precision={i.metadata?.precision || 1}
            placeholder={placeholder}
          />
        )
      }
      case IFormItemTypeEnum.Filelist:
        return (
          <FileUpload
            isEdit={isEdit}
            maxCount={i.maxCount}
            minCount={i.minCount}
            fileSizeLimit={i.fileSizeLimit}
          />
        )
      case IFormItemTypeEnum.Radio:
        return (
          <Radio.Group
            options={i.options}
            disabled={disabled}
          />
        )
      default:
        'error'
    }
  }

  const getInnerComponent = (i: IConfig) => {
    if (!viewNotForm || isEdit) {
      return selectItem(i)
    }
    return <InnerDiv>{getShowData(i.name, initData[i.name]) || '-'}</InnerDiv>
  }

  const initFormList = (i: IConfig) => {
    return (
      <Form.List name={i.name}>
        {(fields, { add, remove }) => (
          <>
            <FormListItem
              label={i.label}
              tooltip={i.tootip}
            >
              {fields.length === 0 ? (
                isEdit ? (
                  <AddBtnWrapper>
                    <GrowpIcon
                      name="Plus"
                      color={theme.brand.text0}
                      size={12}
                    />
                    <Button
                      type="link"
                      onClick={() => add()}
                    >
                      添加选项
                    </Button>
                  </AddBtnWrapper>
                ) : (
                  <NoDataLine>-</NoDataLine>
                )
              ) : (
                fields.map(({ key, name, ...restField }, index: number) => (
                  <Col
                    key={key}
                    span={24}
                  >
                    <FormListWrapper>
                      <DeleteListWrapper>
                        <div>{index + 1}.</div>
                        {isEdit && fields.length > 0 ? (
                          <MinusSpan onClick={() => remove(name)}>
                            删除选项
                          </MinusSpan>
                        ) : null}
                      </DeleteListWrapper>
                      {i.productConf?.map((c) => {
                        const { minCount, maxCount, fileSizeLimit } = c
                        const isLimit = minCount || maxCount
                        return (
                          <FormItem
                            {...restField}
                            key={c.name}
                            label={c.label}
                            tooltip={c.tootip}
                            name={[name, c.name]}
                            valuePropName={selectValuePropsName(c.type)}
                            getValueFromEvent={
                              c.type === 'filelist' ? normFile : undefined
                            }
                            extra={
                              isEdit
                                ? getExtraInfo(c.type, {
                                    minCount,
                                    maxCount,
                                    fileSizeLimit,
                                  })
                                : undefined
                            }
                            rules={
                              isLimit
                                ? [
                                    {
                                      validator: (rule, value, callback) =>
                                        customizeCountValidator(
                                          rule,
                                          value,
                                          callback,
                                          {
                                            max: maxCount,
                                            min: minCount,
                                            type: c.type,
                                          }
                                        ),
                                    },
                                  ]
                                : undefined
                            }
                          >
                            {getInnerComponent(c)}
                          </FormItem>
                        )
                      })}
                    </FormListWrapper>
                  </Col>
                ))
              )}
              {i.label === '证件' ? undefined : isEdit &&
                i.productConf &&
                i.productConf.length > 0 &&
                fields.length > 0 ? (
                <FormItem style={{ marginBottom: 0 }}>
                  <AddBtnWrapper>
                    <GrowpIcon
                      name="Plus"
                      color={theme.brand.text0}
                      size={12}
                    />
                    <Button
                      type="link"
                      onClick={() => add()}
                    >
                      添加选项
                    </Button>
                  </AddBtnWrapper>
                </FormItem>
              ) : null}
            </FormListItem>
          </>
        )}
      </Form.List>
    )
  }

  /**
   * 获取表单项
   *
   * @param i - 表单配置项
   * @returns Form.Item
   */
  const itemRender = (i: IConfig) => {
    const { minCount, maxCount, fileSizeLimit } = i
    const isLimit = minCount || maxCount
    return (
      <Col
        span={colSpan || 24}
        key={i.name}
        id={i.name}
      >
        {i.type === 'product' ? (
          initFormList(i)
        ) : (
          <FormItem
            label={i.label}
            name={i.name}
            valuePropName={selectValuePropsName(i.type)}
            getValueFromEvent={i.type === 'filelist' ? normFile : undefined}
            rules={
              getItemRenderRules(isLimit, i)
              // isLimit
              //   ? [
              //       {
              //         validator: (rule, value, callback) =>
              //           customizeCountValidator(rule, value, callback, {
              //             max: maxCount,
              //             min: minCount,
              //             type: i.type,
              //           }),
              //       },
              //       ...(i.rules ? i.rules : []),
              //     ]
              //   : [...(rulesMap[i.name] || []), ...(i.rules ? i.rules : [])]
            }
            tooltip={i.tootip}
            extra={
              isEdit
                ? getExtraInfo(i.type, { minCount, maxCount, fileSizeLimit })
                : undefined
            }
          >
            {getInnerComponent(i)}
          </FormItem>
        )}
      </Col>
    )
  }
  return (
    <>
      {configs.map((i: IConfig) => {
        return itemRender(i)
      })}
    </>
  )
}

const FormItem = styled(Item)`
  &&& {
    .ant-picker {
      width: 100%;
    }
    .ant-form-item-extra {
      font-size: 12px;
      line-height: 24px;
    }
  }
  .ant-form-item-label > label {
    color: ${(props) => props.theme.text._2};
  }
`

const InnerDiv = styled.div`
  width: 80%;
  color: ${(props) => props.theme.text._1};
`

const AddBtnWrapper = styled.div`
  width: 80px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
`

const MinusSpan = styled.span`
  color: ${(props) => props.theme.status.danger.text};
  cursor: pointer;
`

const FormListItem = styled(FormItem)`
  &&& {
    margin-bottom: 20px;
  }
  .ant-form-item-control-input-content > .ant-col {
    margin-bottom: 12px;
    &:last-child {
      margin-bottom: 0;
    }
  }
`

const FormListWrapper = styled.div`
  border: 1px solid ${(props) => props.theme.brand._5};
  padding: 12px 12px 0 12px;
  border-radius: 8px;
  .ant-form-item {
    margin-bottom: 16px !important;
  }
  .ant-form-item-label {
    width: 100%;
    margin-bottom: 8px;
    display: flex;
    justify-content: flex-start;
    text-align: left;
  }
`

const NoDataLine = styled.span`
  color: ${(props) => props.theme.text._3};
`

const DeleteListWrapper = styled.div`
  width: 100%;
  height: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  & > div {
    min-width: 25px;
    height: 100%;
    border-radius: 4px;
    padding-left: 8px;
    background-color: ${(props) => props.theme.background._3};
    font-size: 12px;
    line-height: 24px;
    color: ${(props) => props.theme.text._1};
  }
`
